/**
 * What built-ins standing against the walls take out of them: wall face
 * area, and baseboard length where they also reach the floor.
 */
import { measuredBuiltIns } from './builtIns';
import { freeElementSpan } from './freeElementTypes';
import { CONTACT_TOLERANCE, facing, freeElementSides } from './geometry';
import type { Segment } from './resolve';
import type { FloorPlanFreeElement } from './schemas';

/** A stretch [from, to] along a wall or up it (ft). */
type Span = readonly [number, number];

/** The total length a set of spans covers, counting each stretch where they overlap once. */
function unionLength(spans: readonly Span[]): number {
  let total = 0, reached = -Infinity;
  for (const [from, to] of [...spans].sort((a, b) => a[0] - b[0])) {
    total += Math.max(0, to - Math.max(from, reached));
    reached = Math.max(reached, to);
  }
  return total;
}

/** Where a built-in stands against one wall: the span along the wall from its start, and the span up it from the floor. */
interface WallFace {
  along: Span;
  up: Span;
}

/**
 * The area a set of faces on one wall covers, counting each patch where they
 * overlap once: between consecutive face ends along the wall, the faces that
 * span that strip union their spans up the wall.
 */
function unionArea(faces: readonly WallFace[]): number {
  const ends = [...new Set(faces.flatMap(face => face.along))].sort((a, b) => a - b);
  let total = 0;
  for (let i = 1; i < ends.length; i++) {
    const spanning = faces.filter(face => face.along[0] <= ends[i - 1] && face.along[1] >= ends[i]);
    total += (ends[i] - ends[i - 1]) * unionLength(spanning.map(face => face.up));
  }
  return total;
}

/** The faces an element's sides press against one wall: parallel to it, within an inch, and overlapping it. */
function wallFaces(element: FloorPlanFreeElement, wall: Segment, ceiling: number): WallFace[] {
  const [bottom, top] = freeElementSpan(element, ceiling);
  const up: Span = [Math.max(0, bottom), Math.min(top, wall.height)];
  return freeElementSides(element).flatMap(side => {
    const contact = facing(wall, side);
    return contact && contact.distance <= CONTACT_TOLERANCE && contact.overlap > 0 ? [{ along: contact.span, up }] : [];
  });
}

/**
 * What built-ins standing against the walls cover: wall face area, and
 * baseboard length where they also reach the floor. Built-ins overlap (a
 * scanned sink inside its cabinet run), so each wall counts what any of them
 * covers once.
 */
export function builtInWallCoverage(
  elements: readonly FloorPlanFreeElement[],
  walls: readonly Segment[],
  ceiling: number,
): { wallArea: number; baseboardLength: number } {
  const builtIns = measuredBuiltIns(elements);
  let wallArea = 0, baseboardLength = 0;
  for (const wall of walls) {
    const faces = builtIns.flatMap(element => wallFaces(element, wall, ceiling));
    wallArea += unionArea(faces);
    baseboardLength += unionLength(faces.filter(face => face.up[0] <= CONTACT_TOLERANCE).map(face => face.along));
  }
  return { wallArea, baseboardLength };
}
